/**
 * Settings → Systems → "Edit…" / "New system…": what the admins know about a system (docs/systems.md) — its name, kind,
 * icon, colour, group (for filtering views), logo, description, owner, vendor, address and aliases — and whether it has been checked. The window is a
 * step in history (`?system=<code>`, `?system=+` for a new one; lib/urlmodal.ts); what is typed is kept in
 * sessionStorage until it is saved. A picked logo is kept only while the window is open (a file is no draft).
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Api, ApiError } from '../lib/api.ts';
import { useFormDraft } from '../lib/formstate.ts';
import { defaultSystemColor, registeredSystems, type SystemInfo } from '../lib/systems.ts';
import { ICONS, ICON_LABEL, KINDS, KIND_LABEL, SYSTEM_LIMITS, SYSTEM_PALETTE, addAliases, codeOf, formOf, formProblems, logoFileProblem, patchOf } from '../lib/systemedit.ts';
import { IconButton, cx } from '../design/primitives.tsx';
import { CloseIcon, SYSTEM_ICON } from './icons.tsx';
import { Button, Field, inputClass } from './ui.tsx';

export const systemDraftKey = (tid: string, code: string | undefined) => `system.${tid}.${code ?? '+'}`;

/** A system's mark: its logo on white, else its icon or code in its colour, in a rounded square. */
export function SystemMark({ code, color, icon, logo, size = 40, className }: {
  code: string; color: string; icon: string | null; logo: string | null; size?: number; className?: string;
}) {
  const Icon = icon ? SYSTEM_ICON[icon] : undefined;
  return (
    <span aria-hidden="true" className={cx('grid place-items-center shrink-0 rounded-[10px] overflow-hidden', logo ? 'bg-white' : '', className)}
      style={{ width: size, height: size, boxShadow: `0 0 0 1.5px ${color}`, background: logo ? undefined : `color-mix(in srgb, ${color} 14%, var(--n2))`, color }}>
      {logo ? <img src={logo} alt="" draggable={false} className="block object-contain" style={{ width: size - 8, height: size - 8 }} />
        : Icon ? <Icon size={Math.round(size * 0.5)} />
          : <span className="font-mono text-micro font-semibold uppercase tracking-[.04em]">{code.length > 4 ? code.slice(0, 3) : code || '?'}</span>}
    </span>
  );
}

type Logo = { file: File; url: string } | 'remove' | undefined;

export function SystemEditor({ tid, system, taken, onClose, onSaved }: {
  tid: string;
  /** The system edited; none = a new one. */
  system?: SystemInfo;
  /** Codes and aliases of the other systems, upper case. */
  taken: ReadonlySet<string>;
  onClose: () => void;
  onSaved: (s: SystemInfo, was: string | undefined) => void;
}) {
  const [f, set, clear] = useFormDraft(systemDraftKey(tid, system?.code), formOf(system));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [logo, setLogo] = useState<Logo>();
  const [logoError, setLogoError] = useState<string>();
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => () => { if (logo && logo !== 'remove') URL.revokeObjectURL(logo.url); }, [logo]);
  const editingCode = !system || system.source === 'manual';
  const problems = formProblems(f, editingCode, taken);
  const bad = Object.keys(problems).length > 0;
  const code = codeOf(f.code) ?? system?.code ?? '';
  const color = f.color || defaultSystemColor(code);
  const custom = !!f.color && !SYSTEM_PALETTE.includes(f.color.toUpperCase());
  const shownLogo = logo === 'remove' ? null : logo ? logo.url : system?.logo ?? null;
  const pick = (file: File | undefined) => {
    if (!file) return;
    const problem = logoFileProblem(file);
    setLogoError(problem ?? undefined);
    if (!problem) setLogo({ file, url: URL.createObjectURL(file) });
  };
  const addTyped = (text: string) => {
    const r = addAliases(text, f.aliases, code);
    set({ aliases: r.aliases, aliasText: r.rest });
  };
  const aliasKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',' || e.key === ' ') && f.aliasText.trim()) { e.preventDefault(); addTyped(f.aliasText); }
    else if (e.key === 'Backspace' && !f.aliasText && f.aliases.length) set({ aliases: f.aliases.slice(0, -1) });
  };
  // The groups already in use, offered as the group is typed
  const knownGroups = useMemo(() => [...new Set([...registeredSystems().values()].map((x) => x.group).filter((g): g is string => !!g))].sort(), []);
  const merging = useMemo(() => f.aliases.filter((a) => registeredSystems().has(a) && !system?.aliases.includes(a)), [f.aliases, system]);
  const save = async () => {
    if (bad) return;
    setBusy(true);
    setFailure(undefined);
    try {
      const body = patchOf(f, system);
      let saved = system ? await Api.updateSystem(tid, system.code, body) : await Api.createSystem(tid, { ...body, code: body.code ?? code });
      if (logo === 'remove' && system?.logo) saved = await Api.deleteSystemLogo(tid, saved.code);
      else if (logo && logo !== 'remove') saved = await Api.putSystemLogo(tid, saved.code, logo.file);
      clear();
      onSaved(saved, system?.code);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content aria-describedby={undefined}
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(680px,94vw)] max-h-[90vh] overflow-auto flex flex-col gap-4 bg-n2 text-n10 rounded-card shadow-float p-5 outline-none mv-pop">
          <div className="flex items-start gap-3">
            <SystemMark code={code} color={color} icon={f.icon || null} logo={shownLogo} />
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
              <Dialog.Title className="m-0 text-body font-semibold [overflow-wrap:anywhere]">{system ? `Details of ${f.name.trim() || system.name}` : 'New system'}</Dialog.Title>
              <span className="text-micro text-n7">
                {system ? <><span className="font-mono">{system.code}</span> · {system.source === 'manual' ? 'added by hand' : 'found in the data'}</> : 'For a system that has sent no data yet, or to name one before it does'}
              </span>
            </div>
            <Dialog.Close asChild><IconButton label="Close"><CloseIcon size={16} /></IconButton></Dialog.Close>
          </div>
          <form className="flex flex-col gap-4" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-[minmax(0,1fr)_180px]">
              <Field label="Name" error={problems.name} hint="As people call it">
                <input className={inputClass} value={f.name} placeholder="e.g. Dynamics 365" maxLength={SYSTEM_LIMITS.name + 20} autoFocus
                  onChange={(e) => set({ name: e.target.value })} aria-invalid={!!problems.name} />
              </Field>
              <Field label="Code" error={problems.code} hint={editingCode ? 'What senders mark data with' : 'From the data: add an alias for another code'}>
                <input className={cx(inputClass, 'font-mono uppercase', !editingCode && 'opacity-70')} value={editingCode ? f.code : system!.code} readOnly={!editingCode}
                  placeholder="ERP" maxLength={24} onChange={(e) => set({ code: e.target.value })} aria-invalid={!!problems.code} />
              </Field>
            </div>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
              <Field label="Kind">
                <select className={inputClass} value={f.kind} onChange={(e) => set({ kind: e.target.value })}>
                  <option value="">Not set</option>
                  {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
              </Field>
              <Field label="Group" error={problems.group} hint="Views can hide a group">
                <input className={inputClass} value={f.group} placeholder="e.g. Monitoring" list="system-groups" data-field="group"
                  onChange={(e) => set({ group: e.target.value })} aria-invalid={!!problems.group} />
                <datalist id="system-groups">{knownGroups.map((g) => <option key={g} value={g} />)}</datalist>
              </Field>
              <Field label="Vendor or product" error={problems.vendor}>
                <input className={inputClass} value={f.vendor} placeholder="e.g. Microsoft" onChange={(e) => set({ vendor: e.target.value })} aria-invalid={!!problems.vendor} />
              </Field>
            </div>

            <div className="flex flex-col gap-1.5 text-small">
              <span className="text-n8" id="sys-icon">Icon</span>
              <div role="radiogroup" aria-labelledby="sys-icon" className="flex items-center gap-1.5 flex-wrap">
                {['', ...ICONS].map((name) => {
                  const Icon = name ? SYSTEM_ICON[name] : undefined;
                  const on = f.icon === name;
                  return (
                    <button key={name || 'none'} type="button" role="radio" aria-checked={on} aria-label={name ? ICON_LABEL[name] : 'No icon: the code'} title={name ? ICON_LABEL[name] : 'No icon: the code'}
                      onClick={() => set({ icon: name })}
                      className={cx('size-9 grid place-items-center rounded-chip border-0 cursor-pointer transition-colors duration-[var(--t-micro)]',
                        on ? 'bg-accent-soft text-accent ring-[1.5px] ring-accent' : 'bg-n3 text-n8 hover:bg-n4 hover:text-n10')}>
                      {Icon ? <Icon size={18} /> : <span className="font-mono text-[10px] font-semibold">ABC</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-1.5 text-small">
              <span className="text-n8" id="sys-color">Colour</span>
              <div role="radiogroup" aria-labelledby="sys-color" className="flex items-center gap-1.5 flex-wrap">
                <button type="button" role="radio" aria-checked={!f.color} onClick={() => set({ color: '' })} title="Automatic"
                  className={cx('h-7 px-2.5 rounded-full border-0 cursor-pointer text-micro font-medium inline-flex items-center gap-1.5',
                    !f.color ? 'bg-accent-soft text-accent ring-[1.5px] ring-accent' : 'bg-n3 text-n8 hover:bg-n4')}>
                  <span className="size-3 rounded-full" style={{ background: defaultSystemColor(code) }} />Automatic
                </button>
                {SYSTEM_PALETTE.map((c) => (
                  <button key={c} type="button" role="radio" aria-checked={f.color.toUpperCase() === c} aria-label={c} title={c} onClick={() => set({ color: c })}
                    className={cx('size-7 rounded-full border-0 cursor-pointer grid place-items-center', f.color.toUpperCase() === c && 'ring-2 ring-offset-2 ring-offset-[var(--n2)]')}
                    style={{ background: c, ['--tw-ring-color' as string]: c }} />
                ))}
                <label title={custom ? `Another colour: ${f.color.toUpperCase()}` : 'Another colour'}
                  className={cx('size-7 rounded-full grid place-items-center cursor-pointer relative overflow-hidden', custom ? 'ring-2 ring-offset-2 ring-offset-[var(--n2)] text-white' : 'bg-n3 text-n8 hover:bg-n4')}
                  style={custom ? { background: f.color, ['--tw-ring-color' as string]: f.color } : undefined}>
                  <span aria-hidden="true" className="text-small leading-none">{custom ? '' : '+'}</span>
                  <input type="color" aria-label="Another colour" className="absolute inset-0 opacity-0 cursor-pointer" value={f.color || defaultSystemColor(code)} onChange={(e) => set({ color: e.target.value.toUpperCase() })} />
                </label>
              </div>
              {problems.color && <span className="text-micro text-fail">{problems.color}</span>}
            </div>

            <div className="flex flex-col gap-1.5 text-small">
              <span className="text-n8">Logo</span>
              <div className="flex items-center gap-3 flex-wrap">
                {shownLogo
                  ? <span className="h-12 min-w-12 max-w-40 px-2 grid place-items-center rounded-chip bg-white shadow-node"><img src={shownLogo} alt="Logo" className="block max-h-10 max-w-36 object-contain" /></span>
                  : <span className="h-12 w-12 grid place-items-center rounded-chip bg-n3 text-micro text-n7">none</span>}
                <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" aria-label="Logo file"
                  onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
                <Button onClick={() => fileInput.current?.click()}>{shownLogo ? 'Replace…' : 'Upload…'}</Button>
                {shownLogo && <Button variant="ghost" onClick={() => { setLogo(system?.logo ? 'remove' : undefined); setLogoError(undefined); }}>Remove</Button>}
                <span className="text-micro text-n7">PNG, JPEG or WebP, at most 200 KB. Shown on the map and in badges.</span>
              </div>
              {logoError && <span className="text-micro text-fail">{logoError}</span>}
            </div>

            <Field label="Description" error={problems.description}>
              <textarea className={cx(inputClass, 'h-auto min-h-20 py-2 resize-y leading-relaxed')} rows={3} value={f.description}
                placeholder="What the system is for, what data it holds" onChange={(e) => set({ description: e.target.value })} aria-invalid={!!problems.description} />
            </Field>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <Field label="Owner" error={problems.owner} hint="Who answers for the system">
                <input className={inputClass} value={f.owner} placeholder="Name or team" onChange={(e) => set({ owner: e.target.value })} aria-invalid={!!problems.owner} />
              </Field>
              <Field label="Address" error={problems.url}>
                <input className={inputClass} type="url" value={f.url} placeholder="https://" onChange={(e) => set({ url: e.target.value })} aria-invalid={!!problems.url} />
              </Field>
            </div>

            <div className="flex flex-col gap-1 text-small">
              <span className="text-n8">Aliases</span>
              <span className="min-h-9 w-full rounded-chip bg-n3 px-1.5 py-1 flex items-center gap-1 flex-wrap focus-within:ring-[1.5px] focus-within:ring-accent">
                {f.aliases.map((a) => (
                  <span key={a} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-n2 font-mono text-micro font-semibold text-n10 shadow-node">
                    {a}
                    <button type="button" aria-label={`Remove alias ${a}`} onClick={() => set({ aliases: f.aliases.filter((x) => x !== a) })}
                      className="size-5 grid place-items-center rounded-full border-0 bg-transparent text-n7 cursor-pointer hover:bg-n3 hover:text-n10"><CloseIcon size={12} /></button>
                  </span>
                ))}
                <input className="flex-1 min-w-32 h-7 bg-transparent border-0 outline-none px-1.5 font-mono text-small uppercase text-n10 placeholder:text-n7 placeholder:normal-case placeholder:font-sans" value={f.aliasText}
                  placeholder={f.aliases.length ? 'Add a code' : 'Other codes for this system, e.g. DYN D365'} aria-label="Add an alias"
                  onChange={(e) => set({ aliasText: e.target.value })} onKeyDown={aliasKey} onBlur={() => { if (f.aliasText.trim()) addTyped(f.aliasText); }} />
              </span>
              <span className={cx('text-micro', problems.aliases ? 'text-fail' : 'text-n7')}>
                {problems.aliases ?? (merging.length
                  ? `${merging.join(', ')} ${merging.length === 1 ? 'is a system' : 'are systems'} of ${merging.length === 1 ? 'its' : 'their'} own now: saving merges ${merging.length === 1 ? 'it' : 'them'} into this one`
                  : 'Data marked with an alias counts as this system everywhere: badges, the map, the data models')}
              </span>
            </div>

            {failure && <p className="m-0 text-small text-fail">{failure}</p>}
            <div className="flex items-center gap-3 flex-wrap pt-1">
              {system?.status === 'new' && (
                <label className="flex items-center gap-2 text-small text-n9 cursor-pointer mr-auto">
                  <input type="checkbox" className="size-4 cursor-pointer accent-[var(--accent)]" checked={f.markChecked} onChange={(e) => set({ markChecked: e.target.checked })} />
                  Mark as checked
                </label>
              )}
              <span className={system?.status === 'new' ? '' : 'flex-1'} />
              <Button variant="ghost" onClick={() => { clear(); onClose(); }}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={busy || bad}>{busy ? 'Saving…' : system ? 'Save' : 'Add system'}</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
